import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router';
import styles from '../CustomOfferDetailsPage.module.css';
import dayjs from 'dayjs';
import CheckIcon from '@mui/icons-material/Check';
import { SimpleModal } from '../../../../components/Modal';
import { StyledButton } from "components/Buttons/DefaultButton/DefaultButton.styles";
import Api from 'api';
import {OfferHygienist, PartialOffer, SkillsProps, UserAccountProps} from "utils/types";
import { Analytics } from 'utils/analytics';
import {exitReact, formatErrorMessage, formatProfileHygienistName} from 'utils';
import * as ls from "local-storage";
import useActionApi from "../../../../hooks/useActionApi";
import FixedLoader from "../../../../components/FixedLoader";
import moment from 'moment';
import { formatPartialOfferTime } from '../../../../utils/timeFormatting';

interface ShiftHours {
  startTime: string;
  endTime: string;
}

interface AcceptOfferProps {
    offerData: {
        isPartialHours: boolean,
        hygienist: OfferHygienist,
        partialOffer: PartialOffer
        skills: SkillsProps[]
    },
    offerId: number,
    originalShiftHours: ShiftHours
    partialShiftHours: ShiftHours
}

interface Button {
    text: string;
    onClick: () => void;
    primary: boolean
}
  
interface ModalContent {
    title: string;
    content: string | JSX.Element;
    buttons: Button[];
}

// @ts-ignore
const TempStars = window.TempStars;

const AcceptOffer: React.FC<AcceptOfferProps> = ({ offerData, offerId, originalShiftHours, partialShiftHours }) => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContent>({title: "", content: "", buttons: []});

  const {partialOffer, hygienist, skills, isPartialHours} = offerData;
  const job = partialOffer?.job;
  const userAccount = ls.get("userAccount") as UserAccountProps;

  const checkRemainingHours = useCallback(() => {
    if (!originalShiftHours.startTime || !originalShiftHours.endTime) return { hasRemainingHours: false };
    
    const jobStart = moment(originalShiftHours.startTime, ['h:mm A', 'HH:mm']);
    const jobEnd = moment(originalShiftHours.endTime, ['h:mm A', 'HH:mm']);
    const offerStart = moment(partialOffer.offeredStartTime, ['h:mm A', 'HH:mm']);
    const offerEnd = moment(partialOffer.offeredEndTime, ['h:mm A', 'HH:mm']);
    const startDiff = offerStart.diff(jobStart, 'hours');
    const endDiff = jobEnd.diff(offerEnd, 'hours');

    return {
      hasRemainingHours: startDiff >= 3 || endDiff >= 3,
      startDiff,
      endDiff
    };
  }, [originalShiftHours, partialOffer]);

  const [acceptPartialOfferLoading, , acceptPartialOffer] = useActionApi({
    apiMethod: Api.acceptPartialOffer,
    onSuccess: (data: any) => {
      Analytics.track('accept offer', offerData);

      const htmlName = formatProfileHygienistName(hygienist, skills);
      const { hasRemainingHours } = checkRemainingHours();
      const formattedStartTime = formatPartialOfferTime(
        partialOffer.offeredStartTime,
        partialOffer.offeredStartTimeLocal,
        partialOffer.location?.lat,
        partialOffer.location?.lon
      );
      const formattedEndTime = formatPartialOfferTime(
        partialOffer.offeredEndTime,
        partialOffer.offeredEndTimeLocal,
        partialOffer.location?.lat,
        partialOffer.location?.lon
      );

      if (hasRemainingHours) {
        setModalContent({
          title: 'Booking Confirmed - Post Remaining Hours?',
          content: <>
            <p>Well done! <span className='blue-text'>{htmlName}</span> is now booked and confirmed for your <span className='red-text'>{hygienist?.type ? 'Dental Assistant' : 'Hygienist'}</span> shift on {dayjs(job?.startDate).format('dddd MMMM D')} from {formattedStartTime} to {formattedEndTime}.</p>
            <p>Would you like to post another shift to cover the remaining hours of your original shift?</p>
          </>,
          buttons: [
            {
              text: 'Yes, Post Another Shift',
              onClick: () => {
                const jobDate = dayjs(job?.startDate);
                history.push('loading');
                history.push('postJob', { 
                  date: jobDate.format('YYYY-MM-DD'),
                  role: hygienist?.type ? '1' : '0'
                });
              },
              primary: true
            },
            {
              text: 'No Thanks',
              onClick: () => {
                exitReact('home');
                setIsModalOpen(false);
              },
              primary: false
            }
          ]
        });
      } else {
        setModalContent({
          title: 'Booked and Confirmed',
          content: <>
            Well done! <span className='blue-text'>{htmlName}</span> is now booked and confirmed for your <span className='red-text'>{hygienist?.type ? 'Dental Assistant' : 'Hygienist'}</span> shift on {dayjs(job?.startDate).format('dddd MMMM D')} from {formattedStartTime} to {formattedEndTime}.
            <br/>You don't have to do anything else.
            <br/><br/>Check your Inbox, we're sending you a confirmation e-mail shortly.
          </>,
          buttons: [
            {
              text: 'I understand',
              onClick: () => {
                exitReact('home');
                setIsModalOpen(false);
              },
              primary: true
            }
          ]
        });
      }
      setIsModalOpen(true);
    },
    onError: (err) => {
      const message = formatErrorMessage(err);
      setModalContent({
        title: 'Error',
        content: message ? `Error accepting offer. ${message}` : 'Error accepting offer. Please try again.',
        buttons: [{text: 'OK', onClick: () => setIsModalOpen(false), primary: true}]
      });
      setIsModalOpen(true);
    }
  });

  const acceptButtonHandler = useCallback(() => {
    if (job?.Autobook && job.hygienistId) {
      setIsModalOpen(false)
      setModalContent({
        title: 'Error',
        content: <>Error accepting offer. Please wait until your
          instabooked {hygienist?.type ? 'assistant' : 'hygienist'} confirms or rejects this shift. You will know in 20
          minutes or less.</>,
        buttons: []
      });
      setIsModalOpen(true);
    } else if (userAccount?.dentist?.cancelledShiftsNum) {
      acceptJobAdditionalConfirmation();
    } else {
      setIsModalOpen(false)
      acceptJobConfirmation();
    }
  }, [job]);

  const cancellationPolicyLink = partialOffer?.location?.country ? '' : <>Please be sure to read our <a
      href={`https://${TempStars.Config.web[(partialOffer?.location?.country ? "US" : "CA")].siteUrl}/short-notice`}
      target="_system" className="external" style={{fontSize: "14px"}}>New Short Notice Cancellation Policy</a>.</>

  const acceptJobAdditionalConfirmation = useCallback(() => {
    setIsModalOpen(false)
    const text = <>
      {dayjs(job?.startDate).format('ddd MMM D, YYYY')}<br/>
      {
        isPartialHours
        ? <>
          <strong>Partial Hours Offered:</strong> {partialShiftHours.startTime} - {partialShiftHours.endTime}<br/>
          <strong>Original Shift Hours:</strong> {originalShiftHours.startTime} - {originalShiftHours.endTime}<br/><br/>
        </>
        : <>{originalShiftHours.startTime} - {originalShiftHours.endTime}<br/><br/></>
      }
      When you Accept an offer, the {hygienist?.type == 1 ? 'Dental Assistant' : 'Dental Hygienist'} is making an
      important commitment to your office and patients, and giving up other opportunities.<br/><br/>
      Cancelling booked shifts hurts everyone - not just the temp, but other offices who might need help but miss out
      because a temp was unnecessarily booked/confirmed.<br/><br/>
      If you Accept this offer, are you confident you'll keep this booking?<br/><br/>
      {cancellationPolicyLink}
    </>;

    setModalContent({
      title: 'Think for a minute.',
      content: text,
      buttons: [
        {
          text: 'YES', onClick: () => {
            setIsModalOpen(false)
            acceptJobConfirmation()
          }, primary: true
        },
        {text: 'Cancel', onClick: () => setIsModalOpen(false), primary: false}
      ]
    });
    setIsModalOpen(true);
  }, [job, originalShiftHours]);

  const acceptJobConfirmation = useCallback(() => {
    const text = <>
      {hygienist?.firstName} {hygienist?.lastName}<br/><br/>
      {dayjs.utc(job?.startDate).format('ddd, MMM D')}<br/>
      {
        isPartialHours
        ? <>
          <strong>Partial Hours Offered:</strong> {partialShiftHours.startTime} - {partialShiftHours.endTime}<br/>
          <strong>Original Shift Hours:</strong> {originalShiftHours.startTime} - {originalShiftHours.endTime}<br/>
        </>
        : <>{originalShiftHours.startTime} - {originalShiftHours.endTime}<br/></>
      }
      Location: {partialOffer?.location?.address}<br/><br/>
      Rate: ${partialOffer?.hourlyRate}/hr<br/><br/>
      By accepting, you are Booking and Confirming this <span className="red-text">
      {hygienist?.type == 1 ? 'Dental Assistant' : 'Hygienist'}
      </span> for this shift at the specified { isPartialHours ? "partial" + " " : ""}hours and rate.<br/><br/>
      Only Accept this offer if you're sure you'll keep the booking.<br/><br/>
      {cancellationPolicyLink}
      <br/><br/>
      Only Accept this offer and book {hygienist?.firstName} if you are sure you will keep your booking commitment once
      you accept it.
    </>;

    setModalContent({
      title: isPartialHours
      ? 'Accept and Book This Partial Hours Offer?'
      : 'Accept and Book This Offer?',
      content: text,
      buttons: [
        {text: 'I Agree & Accept', onClick: () => acceptJob(offerId), primary: true},
        {text: 'I don\'t agree', onClick: () => setIsModalOpen(false), primary: false}
      ]
    });
    setIsModalOpen(true);
  }, [job, originalShiftHours]);

  const acceptJob = useCallback(async (offerId: number) => {
    setIsModalOpen(false)
    await acceptPartialOffer(offerId);
  }, [job, offerData, history, hygienist, skills]);

  return (
      <>
        <StyledButton
            variant="contained"
            color="primary"
            startIcon={<CheckIcon/>}
            size="small"
            id="continue-button"
            className={styles.acceptButton}
            onClick={acceptButtonHandler}
        >
          Accept
        </StyledButton>

        <SimpleModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <h2>{modalContent?.title}</h2>
          <div>{modalContent?.content}</div>
          {modalContent?.buttons && modalContent.buttons.map((button, index) => (
              <StyledButton key={index + button.text} onClick={button.onClick} variant={button.primary ? "contained" : "outlined"}>
                {button.text}
              </StyledButton>
          ))}
        </SimpleModal>

        <FixedLoader loading={acceptPartialOfferLoading}/>
      </>
  );
};

export default AcceptOffer;